import './index.scss';
import LogoColor from 'src/assets/svg/logo_color.svg?react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Divider, Form, Input, Row, Spin } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { nameof } from 'src/shared/utils/objects';
import { IconChevronLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Title, Text, Link, Paragraph } from 'src/shared/components/typography';
import { isEmail } from 'src/shared/utils/strings';
import { useAuth } from 'react-oidc-context';

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPasswordView: React.FC<any> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [success, setSuccess] = useState<boolean>(false);
  const [email, setEmail] = useState('');

  const onFinish = async (values: ForgotPasswordFormValues) => {
    setErrorMsg(undefined);
    setIsLoading(true);

    try {
      const result = await axios.post<any>('/account/forgotPassword', {
        email: values.email
      });
      setSuccess(true);
      if (result.status !== 200) {
        console.error('ForgotPassword', result);
      }
      setIsLoading(false);
    } catch (e) {
      console.error('ForgotPassword', e);
    } finally {
      setSuccess(true);
      setIsLoading(false);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
  };

  return (
    <div id="forgot-password-container">
      <div id="logo-container">
        <span title="back-cta" role="img" id="back-cta" className={isLoading ? 'anticon disabled' : 'anticon'}>
          <IconChevronLeft
            onClick={isLoading ? undefined : () => navigate('/')}
            size={20}
            strokeWidth={3}
            color={isLoading ? 'lightgrey' : '#22817a'}
          />
        </span>
        <LogoColor />
      </div>
      <div id="form-container">
        <Spin spinning={isLoading} size={'large'}>
          <Card>
            {success ? (
              <>
                <Title style={{ fontSize: 24, marginBottom: 22 }} level={4}>
                  {t('¡Correo electrónico enviado!')}
                </Title>
                <Paragraph>
                  <Text style={{ fontSize: 16 }}>{t('Sigue las instrucciones que encontrarás en él y actualiza tu contraseña.')}</Text>
                </Paragraph>
                <Paragraph>
                  <Text style={{ fontSize: 16 }}>
                    {t('Y si ya no quieres cambiarlo, simplemente pulsa en el botón de Inicio de sesión, para entrar en tu cuenta.')}
                  </Text>
                </Paragraph>
                <Row style={{ paddingTop: 18 }}>
                  <Col span={24}>
                    <Button shape="round" style={{ width: '100%' }} size="large" type="primary" onClick={() => auth?.signinRedirect()}>
                      {t('Inicia sesión')}
                    </Button>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Title style={{ fontSize: 24, marginBottom: 22 }} level={4}>
                  {t('¿Olvidaste tu contraseña?')}
                </Title>
                <Text style={{ fontSize: 16 }}>
                  {t('Enviaremos a tu correo electrónico, instrucciones para que puedas volver a entrar en tu cuenta.')}
                </Text>
                {(errorMsg || '').trim().length > 0 && (
                  <Row style={{ marginTop: 24 }}>
                    <Col span={24}>
                      <Alert closable onClose={() => setErrorMsg(undefined)} description={errorMsg} type="error" />
                    </Col>
                  </Row>
                )}

                <Form id="forgotPassword" name="forgotPassword" style={{ maxWidth: '100%', marginTop: 36 }} onFinish={onFinish}>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        style={{ marginBottom: 40 }}
                        name={nameof<ForgotPasswordFormValues>('email')}
                        rules={[{ required: true, message: t('¡Por favor ingrese su nombre de usuario!') as string }]}
                      >
                        <Input
                          className="j2l-form-big-input"
                          disabled={isLoading}
                          size="large"
                          placeholder={t('Correo electrónico') as string}
                          prefix={<MailOutlined />}
                          type="email"
                          name="username"
                          onChange={handleEmailChange}
                          id="username"
                          autoComplete="username"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24}>
                      <Button
                        disabled={!isEmail(email)}
                        shape="round"
                        style={{
                          width: '100%',
                          border: 'none',
                          backgroundColor: !isEmail(email) ? '#F1EDE9' : undefined,
                          color: !isEmail(email) ? '#CBCED4' : undefined
                        }}
                        size="large"
                        type="primary"
                        htmlType="submit"
                      >
                        {t('Envíame instrucciones de acceso')}
                      </Button>
                    </Col>
                  </Row>
                </Form>

                <Divider style={{ marginTop: 40, marginBottom: 35 }} />
                <Row>
                  <Col span={24}>
                    <Text fontSize={16}>{t('¿Ya eres usuario de jobs2live?')}</Text>{' '}
                    <Link onClick={() => auth?.signinRedirect()} disabled={isLoading} color="#1A1C21" fontSize={16} underline>
                      {t('Inicia sesión')}
                    </Link>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default ForgotPasswordView;
